import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import FeedPost from './FeedPost.jsx';
import { getAllFeeds, toggleLike, addComment, getPostComments, editComment, deleteComment, generatePostShareUrl } from '../../../services/feedServices';
import { toast } from 'react-toastify';

const DefaultProfile = '/images/profile/default-profile.png';

const ActionButton = React.memo(({ icon, label, count, onClick, active }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
      active ? 'text-red-500 bg-red-50 hover:bg-red-100' : 'text-font-secondary hover:bg-gray-100 hover:text-font-primary'
    }`}
  >
    <Icon icon={icon} width="20" height="20" />
    {count != null && count > 0 && <span>{count}</span>}
    {label && <span>{label}</span>}
  </button>
));

const Feed = () => {
  const navigate = useNavigate();
  const feedsScrollRef = React.useRef(null);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);
  const [newComment, setNewComment] = useState({});
  const [showComments, setShowComments] = useState({});
  const [postingNewPost, setPostingNewPost] = useState(false);

  // Comments state
  const [postComments, setPostComments] = useState({}); // Store comments for each post
  const [commentsLoading, setCommentsLoading] = useState({}); // Loading state for comments
  const [editingComment, setEditingComment] = useState({}); // Track which comment is being edited
  const [editCommentText, setEditCommentText] = useState({}); // Store edit text for each comment
  const [userProfile, setUserProfile] = useState(null);
  const [showFullText, setShowFullText] = useState({}); // Track which posts show full text

  const user_id = JSON.parse(localStorage.getItem('user')).id;

  // Prevent scroll chaining
  useEffect(() => {
    const scrollContainer = feedsScrollRef.current;
    if (!scrollContainer) return;

    const handleWheel = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

      if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
        e.preventDefault();
      }
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });
    return () => scrollContainer.removeEventListener('wheel', handleWheel);
  }, []);

  // Load initial feeds
  const loadFeeds = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) setLoading(true);
      else {
        setLoadingMore(true);
        setShowLoadingAnimation(true);
      }

      const response = await getAllFeeds(page, 5);

      if (response.success) {
        const newPosts = response.data.posts.map(post => ({
          id: post.id,
          user: {
            name: post.user_name,
            avatar: post.user_avatar || DefaultProfile
          },
          content: post.description,
          media: post.media_url ? {
            type: post.media_type,
            url: post.media_url
          } : null,
          isLiked: post.is_liked_by_user === 1,
          likes: post.likes_count,
          comments: [], // Comments will be loaded separately
          commentsCount: post.comments_count,
          created_at: post.created_at
        }));

        if (append) {
          // Add 1 second delay for smooth loading animation
          setTimeout(() => {
            setPosts(prev => [...prev, ...newPosts]);
            setLoadingMore(false);
            setShowLoadingAnimation(false);
          }, 1000);
        } else {
          setPosts(newPosts);
          setLoading(false);
        }

        setHasMore(response.data.pagination.has_more);
        setCurrentPage(page);
        
        // Set user profile if available
        if (response.data.user_profile) {
          setUserProfile(response.data.user_profile);
        }
      } else {
        setLoadingMore(false);
        setShowLoadingAnimation(false);
      }
    } catch (error) {
      console.error('Error loading feeds:', error);
      setLoadingMore(false);
      setShowLoadingAnimation(false);
    } finally {
      if (!append) {
        setLoading(false);
      }
    }
  }, []);

  // Load more posts for infinite scroll
  const loadMorePosts = useCallback(() => {
    if (!loadingMore && hasMore) loadFeeds(currentPage + 1, true);
  }, [loadFeeds, loadingMore, hasMore, currentPage]);

  // Initial load
  useEffect(() => {
    loadFeeds();
  }, [loadFeeds]);

  // Event handlers
  const handleLike = async (postId) => {
    try {
      const response = await toggleLike(postId);
      if (response.success) {
        setPosts(posts.map(post =>
          post.id === postId
            ? {
                ...post,
                isLiked: response.data.is_liked,
                likes: response.data.likes_count
              }
            : post
        ));
      } else {
        console.error('Failed to update like');
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  // Load comments for a specific post
  const loadPostComments = useCallback(async (postId) => {
    try {
      setCommentsLoading(prev => ({ ...prev, [postId]: true }));

      // Load all comments at once by setting a high page size
      const response = await getPostComments(postId, 1, 1000);

      if (response.success) {
        const newComments = response.data.comments.map(comment => ({
          id: comment.id,
          user: comment.user_name,
          avatar: comment.user_avatar || DefaultProfile,
          text: comment.comment,
          timestamp: new Date(comment.commented_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          user_id: comment.user_id // Add user_id for permission checks
        }));

        setPostComments(prev => ({
          ...prev,
          [postId]: newComments
        }));
      } else {
          console.error('Failed to load comments');
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setCommentsLoading(prev => ({ ...prev, [postId]: false }));
    }
  }, []);

  const handleComment = (postId) => {
    const isOpening = !showComments[postId];
    setShowComments(prev => ({ ...prev, [postId]: !prev[postId] }));

    // Load comments when opening comments section for the first time
    if (isOpening && !postComments[postId]) {
      loadPostComments(postId);
    }
  };





  const handleSubmitComment = async (postId) => {
    const commentText = newComment[postId];
    if (!commentText?.trim()) return;
    if (commentText.length > 400) return;

    try {
      const response = await addComment(postId, commentText.trim());
      if (response.success) {
        // Update the post's comments count
        setPosts(posts.map(post =>
          post.id === postId
            ? { ...post, commentsCount: post.commentsCount + 1 }
            : post
        ));

        // Add the new comment to the comments list
        const newCommentObj = {
          id: response.data.comment.id,
          user: response.data.comment.user_name,
          avatar: response.data.comment.user_avatar || DefaultProfile,
          text: response.data.comment.comment,
          timestamp: 'Just now',
          user_id: user_id // Add user_id for permission checks
        };

        setPostComments(prev => ({
          ...prev,
          [postId]: [newCommentObj, ...(prev[postId] || [])]
        }));

        setNewComment(prev => ({ ...prev, [postId]: '' }));
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleEditComment = async (postId, commentId) => {
    const editText = editCommentText[commentId];
    if (!editText?.trim()) return;
    if (editText.length > 400) return;

    try {
      const response = await editComment(commentId, editText.trim());
      if (response.success) {
        // Update the comment in the comments list
        setPostComments(prev => ({
          ...prev,
          [postId]: prev[postId].map(comment =>
            comment.id === commentId
              ? { ...comment, text: editText.trim() }
              : comment
          )
        }));

        setEditingComment(prev => ({ ...prev, [commentId]: false }));
        setEditCommentText(prev => ({ ...prev, [commentId]: '' }));
      }
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    try {
      const response = await deleteComment(commentId);
      if (response.success) {
        // Update the post's comments count
        setPosts(posts.map(post =>
          post.id === postId
            ? { ...post, commentsCount: post.commentsCount - 1 }
            : post
        ));

        // Remove the comment from the comments list
        setPostComments(prev => ({
          ...prev,
          [postId]: prev[postId].filter(comment => comment.id !== commentId)
        }));
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handlePostSubmit = async () => {
    setPostingNewPost(true);
    try {
      await loadFeeds(1, false);
    } catch (error) {
      console.error('Error refreshing feed:', error);
    } finally {
      setPostingNewPost(false);
    }
  };



  const handleMyFeedClick = () => {
    navigate('/user/my-feed');
  };

  const handleShare = async (post) => {
    try {
      // Generate shareable URL for the post
      const response = await generatePostShareUrl(post.id);

      if (response.success) {
        const shareUrl = response.data.shareUrl;

        // Prepare share data
        const shareData = {
          title: `${post.user.name}'s Post`,
          text: post.content || 'Check out this post!',
          url: shareUrl
        };

        // Check if Web Share API is supported
        if (navigator.share) {
          await navigator.share(shareData);
          toast.success('Shared successfully');
        } else {
          await navigator.clipboard.writeText(shareUrl);
          toast.success('Link copied to clipboard');
        }
      } else {
        toast.error('Could not generate share link');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error('Could not share');
      }
    }
  };

  const renderMedia = (media) => {
    if (!media) return null;
    if (media.type === 'image') {
      return (
        <img
          src={media.url}
          alt="Post"
          className="w-full max-h-[420px] object-cover rounded-xl bg-gray-100 mt-3"
        />
      );
    }
    if (media.type === 'video') {
      return (
        <video controls className="w-full max-h-[420px] rounded-xl bg-black mt-3">
          <source src={media.url} type="video/mp4" />
        </video>
      );
    }
    return null;
  };

  const renderPostContent = (content, post) => {
    if (!content) return null;
    const hasMedia = post.media && (post.media.type === 'image' || post.media.type === 'video');
    if (!hasMedia) {
      return <p className="text-gray-800 text-base leading-relaxed whitespace-pre-wrap break-words">{content}</p>;
    }
    const shouldTruncate = content.length > 120;
    const isShowingFull = showFullText[post.id];
    const displayText = isShowingFull ? content : content.slice(0, 120) + (shouldTruncate ? '...' : '');
    return (
      <p className="text-gray-800 text-base leading-relaxed whitespace-pre-wrap break-words">
        {displayText}
        {shouldTruncate && (
          <button
            type="button"
            className="ml-1 text-primary font-medium hover:underline"
            onClick={() => setShowFullText(prev => ({ ...prev, [post.id]: !isShowingFull }))}
          >
            {isShowingFull ? ' Show less' : ' Show more'}
          </button>
        )}
      </p>
    );
  };

  // Memoized comment handlers to prevent re-renders
  const handleCommentChange = useCallback((postId, value) => {
    setNewComment(prev => ({ ...prev, [postId]: value }));
  }, []);

  const handleCommentKeyDown = useCallback((e, postId) => {
    if (e.key === 'Enter') {
      handleSubmitComment(postId);
    }
  }, [handleSubmitComment]);

  const handleCommentSubmitClick = useCallback((postId) => {
    handleSubmitComment(postId);
  }, [handleSubmitComment]);

  const handleEditCommentChange = useCallback((commentId, value) => {
    setEditCommentText(prev => ({ ...prev, [commentId]: value }));
  }, []);

  const handleEditCommentKeyDown = useCallback((e, postId, commentId) => {
    if (e.key === 'Enter') {
      handleEditComment(postId, commentId);
    }
  }, [handleEditComment]);

  const renderComments = (post) => {
    if (!showComments[post.id]) return null;
    const comments = postComments[post.id] || [];
    const isLoading = commentsLoading[post.id];

    return (
      <div className="border-t border-gray-100 pt-4 mt-4">
        <p className="text-sm font-semibold text-font-primary mb-3">Comments ({post.commentsCount || 0})</p>

        <div className="flex gap-3 mb-4">
          <img src={DefaultProfile} alt="" className="w-9 h-9 rounded-full object-cover shrink-0 border border-gray-200" />
          <div className="flex-1 min-w-0 flex gap-2">
            <input
              type="text"
              className="flex-1 min-w-0 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="Write a comment..."
              value={newComment[post.id] || ''}
              onChange={(e) => handleCommentChange(post.id, e.target.value)}
              onKeyDown={(e) => handleCommentKeyDown(e, post.id)}
              maxLength={400}
            />
            <button
              type="button"
              className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              onClick={() => handleCommentSubmitClick(post.id)}
              disabled={!newComment[post.id]?.trim()}
              aria-label="Send comment"
            >
              <Icon icon="mdi:send" width="18" height="18" />
            </button>
          </div>
        </div>
        <p className="text-xs text-font-secondary mb-3">{(newComment[post.id] || '').length}/400</p>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" aria-hidden />
          </div>
        ) : (
          <div className="space-y-3">
            {comments.map((comment) => (
              <div key={comment.id} className="flex gap-3">
                <img src={comment.avatar} alt="" className="w-8 h-8 rounded-full object-cover shrink-0 border border-gray-200" />
                <div className="flex-1 min-w-0 rounded-xl bg-gray-50 border border-gray-100 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium text-font-primary text-sm">{comment.user}</span>
                    {comment.user_id === user_id && (
                      <div className="flex gap-1 shrink-0">
                        {editingComment[comment.id] ? (
                          <>
                            <button type="button" className="p-1.5 rounded-lg text-green-600 hover:bg-green-50" onClick={() => handleEditComment(post.id, comment.id)} aria-label="Save"><Icon icon="mdi:check" width="16" height="16" /></button>
                            <button type="button" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200" onClick={() => { setEditingComment(prev => ({ ...prev, [comment.id]: false })); setEditCommentText(prev => ({ ...prev, [comment.id]: '' })); }} aria-label="Cancel"><Icon icon="mdi:close" width="16" height="16" /></button>
                          </>
                        ) : (
                          <>
                            <button type="button" className="p-1.5 rounded-lg text-primary hover:bg-primary/10" onClick={() => { setEditingComment(prev => ({ ...prev, [comment.id]: true })); setEditCommentText(prev => ({ ...prev, [comment.id]: comment.text })); }} aria-label="Edit"><Icon icon="mdi:pencil" width="14" height="14" /></button>
                            <button type="button" className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" onClick={() => handleDeleteComment(post.id, comment.id)} aria-label="Delete"><Icon icon="mdi:delete" width="14" height="14" /></button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {editingComment[comment.id] ? (
                    <div className="mt-2">
                      <input
                        type="text"
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                        value={editCommentText[comment.id] || ''}
                        onChange={(e) => handleEditCommentChange(comment.id, e.target.value)}
                        onKeyDown={(e) => handleEditCommentKeyDown(e, post.id, comment.id)}
                        maxLength={400}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <p className="text-sm text-font-secondary mt-1 break-words whitespace-pre-wrap">{comment.text}</p>
                  )}
                  <p className="text-xs text-font-secondary mt-1">{comment.timestamp}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };



  return (
    <div className="flex flex-col h-full">
      {/* Fixed Header */}
      <div className="flex items-center justify-between gap-4 mb-6 shrink-0 max-w-2xl mx-auto w-full">
        <h1 className="text-2xl md:text-3xl font-semibold text-[var(--text-primary)]">Posts</h1>
        <button
          type="button"
          onClick={handleMyFeedClick}
          className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white border border-[var(--border-color)] hover:border-[var(--primary-lighter)] hover:shadow-md transition-all"
        >
          <img
            src={userProfile?.profile_pic_url || DefaultProfile}
            alt=""
            className="w-9 h-9 rounded-full object-cover border-2 border-gray-100 shrink-0"
          />
          <div className="hidden sm:block min-w-0">
            <p className="font-semibold text-[var(--text-primary)] text-sm truncate">{userProfile?.name || 'User'}</p>
            <p className="text-xs text-[var(--text-secondary)]">View your posts</p>
          </div>
          <Icon icon="mdi:chevron-right" className="w-5 h-5 text-[var(--text-secondary)] shrink-0" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div ref={feedsScrollRef} className="flex-1 overflow-y-auto -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div className="space-y-3 pb-6">
          {/* Create Post */}
          <div className="max-w-2xl mx-auto w-full">
            <FeedPost onPostSubmit={handlePostSubmit} userProfile={userProfile} />
          </div>

          {postingNewPost && (
            <div className="max-w-2xl mx-auto w-full bg-white rounded-xl border-2 border-[var(--border-color)] p-6 text-center">
              <div className="w-8 h-8 border-2 border-[var(--primary-color)] border-t-transparent rounded-full animate-spin mx-auto mb-2" aria-hidden />
              <p className="font-medium text-[var(--primary-color)] text-sm">Creating your post...</p>
              <p className="text-xs text-[var(--text-secondary)] mt-1">Please wait</p>
            </div>
          )}

          {/* Posts Feed */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-10 h-10 border-2 border-[var(--primary-color)] border-t-transparent rounded-full animate-spin" aria-hidden />
              <p className="mt-4 text-sm font-medium text-[var(--text-secondary)]">Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="max-w-2xl mx-auto w-full bg-white rounded-xl border-2 border-[var(--border-color)] p-12 text-center">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gray-100 text-gray-400 mb-3">
                <Icon icon="mdi:post-outline" width="32" height="32" />
              </span>
              <p className="text-base font-medium text-[var(--text-primary)] mb-1">No posts yet</p>
              <p className="text-sm text-[var(--text-secondary)]">Be the first to share something with the community.</p>
            </div>
          ) : (
            <>
              <div className="max-w-2xl mx-auto w-full space-y-2">
                {posts.map((post) => (
                  <article
                    key={post.id}
                    className="bg-white rounded-xl shadow-sm border-2 border-[var(--border-color)] overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* make the padding bottom to 4 */}
                    <div className="p-4 pt-2">
                      <div className="flex items-center gap-2.5 mb-3">
                        <img
                          src={post.user.avatar}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover border-2 border-gray-100 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-[var(--text-primary)] truncate">{post.user.name}</p>
                          <p className="text-sm text-[var(--text-secondary)]">
                            {new Date(post.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="mb-3">
                        {renderPostContent(post.content, post)}
                        {renderMedia(post.media)}
                      </div>

                      <div className="flex items-center gap-0.5 pt-2 pd border-t border-[var(--border-color)] space-x-2">
                        <ActionButton
                          icon={post.isLiked ? 'mdi:heart' : 'mdi:heart-outline'}
                          label="Like"
                          count={post.likes}
                          onClick={() => handleLike(post.id)}
                          active={post.isLiked}
                        />
                        <ActionButton
                          icon="mdi:comment-outline"
                          label="Comment"
                          count={post.commentsCount || 0}
                          onClick={() => handleComment(post.id)}
                        />
                        <ActionButton
                          icon="mdi:share-variant-outline"
                          label="Share"
                          onClick={() => handleShare(post)}
                        />
                      </div>

                      {renderComments(post)}
                    </div>
                  </article>
                ))}
              </div>

              {showLoadingAnimation && (
                <div className="max-w-2xl mx-auto flex flex-col items-center py-8">
                  <div className="w-8 h-8 border-2 border-[var(--primary-color)] border-t-transparent rounded-full animate-spin" aria-hidden />
                  <p className="mt-3 text-sm font-medium text-[var(--primary-color)]">Loading more...</p>
                </div>
              )}

              {!showLoadingAnimation && !loadingMore && hasMore && (
                <div className="max-w-2xl mx-auto text-center pt-5">
                  <button
                    type="button"
                    onClick={loadMorePosts}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-[var(--primary-color)] border-2 border-[var(--primary-color)] hover:bg-[var(--primary-color)] hover:text-white transition-colors"
                  >
                    <Icon icon="mdi:chevron-down" width="20" height="20" />
                    Load more
                  </button>
                </div>
              )}

              {!hasMore && posts.length > 0 && (
                <p className="max-w-2xl mx-auto text-center text-sm text-[var(--text-secondary)] py-8">You've reached the end</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Feed;